import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { ArticleNav } from "@/components/articles/ArticleNav";
import { ArticleShareBar } from "@/components/articles/ArticleShareBar";
import { RegistryNotice } from "@/components/articles/RegistryNotice";

const DoctorsShortArticle = () => {
  const path = "/articles/644-doctors-short";
  const url = `https://davidisei.com${path}`;
  const title = "You Are 644 Doctors Short. The Number Is Getting Worse.";
  const description =
    "Hawaiʻi has 12,688 licensed physicians but only 3,044 practicing FTE — a 644-physician shortfall driven by retention, not recruitment.";

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "OpinionNewsArticle",
    headline: title,
    description,
    author: {
      "@type": "Person",
      name: "David Isei",
      jobTitle: "Executive Director, Hawaiʻi Healthcare Task Force",
    },
    datePublished: "2026-05-25",
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    articleSection: "Workforce",
    inLanguage: "en-US",
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>You Are 644 Doctors Short | David Isei</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <article className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link
            to="/articles"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Articles
          </Link>

          <div className="text-[11px] uppercase tracking-[0.2em] text-accent mb-4">
            Hawaiʻi Healthcare Intelligence · Workforce
          </div>

          <h1 className="font-display text-3xl md:text-5xl font-bold text-foreground leading-tight mb-4">
            You Are 644 Doctors Short. The Number Is Getting Worse.
          </h1>
          <p className="font-display text-xl md:text-2xl text-muted-foreground leading-snug mb-8 italic">
            Hawaiʻi has 12,688 licensed physicians. Only 3,044 are actually practicing full-time. The
            shortage is a retention problem, not a recruiting one.
          </p>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground border-y border-border/40 py-4 mb-6">
            <span className="inline-flex items-center gap-2 text-accent uppercase tracking-[0.18em] text-[13px] font-semibold">
              <User className="h-4 w-4" />
              David Isei · Hawaiʻi Healthcare Task Force
            </span>
            <span className="inline-flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              May 2026 · 6 min read
            </span>
          </div>

          <ArticleShareBar path={path} title={title} description={description} />

          <RegistryNotice
            items={[
              "Additional data, October 2026: the medical school's preliminary 2026 count, reported in September, puts Hawaiʻi County about 34 percent short and finds that in the past year more than 46 physicians left the state, 20 to 30 retired, and 10 died. The 2025 counts in this piece were higher. The full 2026 report is expected in December.",
            ]}
          />
          <div className="prose prose-lg dark:prose-invert max-w-none font-body">
            <p>
              Hawaiʻi has 12,688 licensed physicians. Only 3,647 of them are currently seeing
              patients. That gap is not a clerical quirk. It represents the distance between a
              credential on file and a doctor who will actually pick up the phone when you call.
            </p>

            <p>
              When part-time practice is counted at full-time equivalent, the true practicing
              workforce drops to 3,044. Against current patient demand, Hawaiʻi is short 644
              physician FTE. Factor in the reality that different islands have different specialties
              available in different quantities, and the shortfall reaches 833.
            </p>

            <p>
              These numbers come from the{" "}
              <a href="https://jabsom.hawaii.edu" target="_blank" rel="noopener noreferrer">
                JABSOM AHEC Hawaiʻi Physician Workforce Assessment, December 2025
              </a>{" "}
              and a{" "}
              <a
                href="https://www.civilbeat.org/2026/01/hawaii-physician-shortage-getting-worse/"
                target="_blank"
                rel="noopener noreferrer"
              >
                January 2026 Honolulu Civil Beat investigation
              </a>
              . They are not projections. They are the current state of the system.
            </p>

            <h2>Where the Gaps Are Largest</h2>
            <p>
              Hawaiʻi County, the Big Island, needs 224 more physicians. That is about 43 percent short of the physicians the county needs. Maui County needs 179 more and has the largest primary care deficit of
              any county in the state: 45 primary care physicians short. Statewide, primary care is
              178 FTE below what patients need.
            </p>

            <p>
              Neighbor island physician supply runs 30–43% below Oʻahu levels, according to{" "}
              <a href="https://jabsom.hawaii.edu/ahec" target="_blank" rel="noopener noreferrer">
                JABSOM's Area Health Education Centers program
              </a>
              . That gap is not static. A quarter of Hawaiʻi's current practicing physicians are 65
              or older. In 2025, more than 88 left the state and more than 81 retired. The pipeline
              adding new physicians is not keeping up with the departures.
            </p>

            <h2>Why Physicians Are Leaving</h2>
            <p>
              The instinctive answer to a doctor shortage is to recruit more doctors. Hawaiʻi has
              been running recruitment programs for years. The shortage is getting worse. That tells
              you the problem is not on the supply entry side. It is on the retention side.
            </p>

            <p>Three conditions are driving physician departure.</p>

            <p>
              Hawaiʻi is the only state in the country that taxes physician private practice revenue
              through a gross receipts tax called the General Excise Tax. The combined rate is 4.5%.
              For Medicare and Medicaid patients, physicians cannot legally pass that tax to
              patients. They absorb it. A{" "}
              <a href="https://www.brown.edu/ujph" target="_blank" rel="noopener noreferrer">
                Brown University Journal of Public Health analysis
              </a>{" "}
              found this effectively costs the average family practice 15–20% of its revenue from
              government-payer patients. Governor Green signed Act 47 in June 2024, which removes
              the GET from Medicare, Medicaid, and TRICARE reimbursements starting January 1, 2026.
              That is a meaningful step. The tax on commercial insurance revenue remains.
            </p>

            <p>
              Hawaiʻi's Medicare reimbursement rate does not reflect what it actually costs to
              practice medicine here. The federal formula that adjusts Medicare payments for local
              cost has Hawaiʻi at 1.06. Alaska, with a similar geography and isolation profile, is
              at 1.50. Hawaiʻi physicians collect less per Medicare patient than physicians in many
              lower-cost states.
            </p>

            <p>
              The median home in Honolulu costs over $900,000. A physician finishing residency with
              $200,000 or more in student loans, entering a market with the tax and reimbursement
              conditions above, often decides to practice somewhere that pencils out. Many of them
              trained at JABSOM. Hawaiʻi paid to educate them and then exported them because the
              economics here did not work.
            </p>

            <h2>The 2030 Projection</h2>
            <p>
              Research published in the{" "}
              <a href="https://www.brown.edu/ujph" target="_blank" rel="noopener noreferrer">
                Brown University Journal of Public Health
              </a>{" "}
              projects a Grade D physician shortage for Hawaiʻi by 2030. That is the trajectory if
              current conditions continue. The state received a $188,892,440 federal Rural Health
              Transformation Program award in December 2025, the largest federal healthcare
              infrastructure investment in Hawaiʻi's recent history. That funding creates five years
              of runway to modernize the system. It does not, by itself, change the economic
              conditions that are driving physicians out.
            </p>

            <h2>What Needs to Happen</h2>
            <p>
              The state needs to extend Act 47's GET exemption to commercial insurance revenue. The
              congressional delegation needs to push the PATCH Act forward to fix the Medicare
              reimbursement formula. Loan repayment programs need to be sized to Hawaiʻi's actual
              cost-of-living gap, not continental benchmarks. And Hawaiʻi needs island-specific
              workforce tracking, because the statewide numbers hide how bad the situation is on
              Maui and the Big Island specifically.
            </p>

            <p>
              The Hawaiʻi Healthcare Task Force and HHIP track physician supply conditions and
              workforce trends as part of the independent intelligence work we do for the state. If
              you are a policymaker, a health system leader, or a community organization that wants
              to understand what these numbers mean for your island, we want to hear from you.
            </p>

            <p>
              <Link to="/connect">Learn more about HHIP and the Hawaiʻi Healthcare Task Force →</Link>
            </p>

            <p className="text-xs text-muted-foreground italic mt-10 border-t border-border/40 pt-6">
              David Isei is the Executive Director of the Hawaiʻi Healthcare Task Force and
              architect of the Hawaiʻi Healthcare Intelligence Platform (HHIP).
            </p>
          </div>

          <ArticleNav currentSlug="644-doctors-short" />
        </motion.div>
      </article>
    </div>
  );
};

export default DoctorsShortArticle;
